if (!window.customElements.get('media-grid-carousel')) {

    class MediaGridCarousel extends HTMLElement {
        constructor() {
            super();
            this.slider = null;
            this.hasInitialized = false;
            this.isAtStart = true;
            this.isAtEnd = false;
            this.isMobile = window.innerWidth <= 991;
            this.resizeTimeout = null;
            this.arrow = null;
            this.container = null;
        }

        connectedCallback() {
            this.container = this.querySelector('.keen-slider');
            if (!this.container || this.container.children.length < 2) return;

            this.container.querySelectorAll(".media-grid__item").forEach(item => {
                item.classList.add("keen-slider__slide");
            });

            // Initialize based on current screen size
            this.handleResize();

            // Add resize listener
            window.addEventListener('resize', this.handleResize.bind(this));
        }

        handleResize() {
            // Debounce resize events
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }

            this.resizeTimeout = setTimeout(() => {
                const wasMobile = this.isMobile;
                this.isMobile = window.innerWidth <= 991;

                // If mobile state changed, handle the transition
                if (wasMobile !== this.isMobile) {
                    if (this.isMobile) {
                        // Transitioning to mobile - initialize slider
                        this.initializeSlider();
                    } else {
                        // Transitioning to desktop - destroy slider
                        this.destroySlider();
                    }
                } else if (this.isMobile && !this.slider) {
                    // Already mobile but no slider - initialize
                    this.initializeSlider();
                } else if (!this.isMobile && !this.hasInitialized) {
                    // Already desktop but not initialized - add class
                    this.addDesktopClass();
                }
            }, 250); // 250ms debounce
        }

        addDesktopClass() {
            setTimeout(() => {
                this.container.classList.add("keen-slider--initialized");
                this.hasInitialized = true;
            }, 300);
        }

        initializeSlider() {
            if (this.slider) {
                this.destroySlider();
            }

            whenReady('KeenSlider', () => {
                try {
                    this.setupSlider();
                } catch (error) {
                    console.error('Failed to initialize KeenSlider:', error);
                }
            });
        }

        setupSlider() {
            let isInNavigationZone = false;

            // Wheel Controls function (from Keen Slider example)
            const WheelControls = (slider) => {
                let touchTimeout;
                let position;
                let wheelActive;

                function dispatch(e, name) {
                    position.x -= e.deltaX;
                    position.y -= e.deltaY;
                    slider.container.dispatchEvent(
                        new CustomEvent(name, {
                            detail: {
                                x: position.x,
                                y: position.y,
                            },
                        })
                    );
                }

                function wheelStart(e) {
                    position = {
                        x: e.pageX,
                        y: e.pageY,
                    };
                    dispatch(e, "ksDragStart");
                }

                function wheel(e) {
                    dispatch(e, "ksDrag");
                }

                function wheelEnd(e) {
                    dispatch(e, "ksDragEnd");
                }

                function eventWheel(e) {
                    e.preventDefault();
                    if (!wheelActive) {
                        wheelStart(e);
                        wheelActive = true;
                    }
                    wheel(e);
                    clearTimeout(touchTimeout);
                    touchTimeout = setTimeout(() => {
                        wheelActive = false;
                        wheelEnd(e);
                    }, 50);
                }

                slider.on("created", () => {
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    if (!isSafari) {
                        slider.container.addEventListener("wheel", eventWheel, {
                            passive: false,
                        });
                    }
                });
            };

            // Progress nav logic
            const progressNav = this.querySelector('.keen-slider__progress-nav');
            const progressBar = progressNav ? progressNav.querySelector('.keen-slider__progress-bar') : null;
            const slides = this.container.querySelectorAll('.keen-slider__slide');
            // Responsive slidesInView calculation (like collection-carousel.js)
            const slidesInView = window.innerWidth > 991 ? 4 : window.innerWidth > 767 ? 3 : 1;

            // Add click handler to progress nav
            if (progressNav) {
                progressNav.addEventListener('click', (e) => {
                    if (!this.slider) return;
                    // Get the click position within the nav
                    const rect = progressNav.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const clickPercentage = clickX / rect.width;
                    // Calculate the target slide index based on click position
                    const maxIdx = this.slider.track.details.maxIdx;
                    const targetSlideIndex = Math.floor(clickPercentage * maxIdx);
                    // Ensure we don't exceed the maximum index
                    const clampedIndex = Math.min(targetSlideIndex, maxIdx);
                    this.slider.moveToIdx(clampedIndex);
                });
            }

            // Update progress bar position (match collection-carousel.js)
            const updateProgressBar = (relIdx) => {
                if (!progressBar) return;
                // Use the slider's maxIdx instead of slides.length to account for placeholders
                const maxIdx = this.slider ? this.slider.track.details.maxIdx : slides.length - slidesInView;
                const isAtEnd = relIdx >= maxIdx;
                // Calculate progress percentage
                const progressPercentage = isAtEnd ? 100 : (relIdx / maxIdx) * 100;
                // Get the progress nav container to calculate available width
                const progressNavWidth = progressNav.offsetWidth + 8;
                const barWidth = window.innerWidth > 991 ? 235 : 85;
                const maxTranslateX = progressNavWidth - barWidth; // Maximum translation
                // Calculate the translateX position
                const translateX = (progressPercentage / 100) * maxTranslateX;
                // Update the progress bar position
                if (window.gsap) {
                    window.gsap.to(progressBar, { 
                        x: translateX, 
                        duration: 0.3, 
                        ease: "expo.out" 
                    });
                } else {
                    progressBar.style.transform = `translateX(${translateX}px)`;
                    progressBar.style.transition = 'none';
                    progressBar.style.opacity = '1';
                }
            };

            const sliderConfig = {
                loop: false,
                rubberband: false,
                slides: {
                    perView: "auto",
                    spacing: 16
                },
                defaultAnimation: {
                    duration: 1000,
                    easing: (t) => {
                        return 1 - Math.pow(1 - t, 7);
                    }
                },  
                dragSpeed: 1.2,
                dragAngle: 45,
                created(s) {
                    // Ensure slides are properly sized
                    // s.slides.forEach(slide => {
                    //     slide.style.flex = '0 0 auto';
                    // });
                },
                detailsChanged: (s) => {
                    this.isAtStart = s.track.details.rel === 0;
                    this.isAtEnd = s.track.details.rel === s.track.details.maxIdx;
                    this.handleSlideChange(s, this.container);
                    updateProgressBar(s.track.details.rel);
                }
            };

            this.slider = new window.KeenSlider(this.container, sliderConfig, [WheelControls]);

            // Initialize progress nav on first load
            updateProgressBar(0);

            // Prevent link clicks in navigation zones
            this.container.addEventListener('click', (e) => {

                if(window.matchMedia('(hover: none)').matches) {
                    return;
                }

                const rect = this.container.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const quarter = Math.floor(x / (rect.width / 4));
                if (quarter === 0 && !this.isAtStart) {
                    e.preventDefault();
                    this.slider.prev();
                } else if (quarter === 3 && !this.isAtEnd) {
                    e.preventDefault();
                    this.slider.next();
                }
                // Otherwise, allow normal link clicks
            }, true);

            // Add support for external arrow buttons
            const prevArrow = this.querySelector('.keen__slider-arrow-prev');
            const nextArrow = this.querySelector('.keen__slider-arrow-next');
            if (prevArrow) {
                prevArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.slider) this.slider.prev();
                });
            }
            if (nextArrow) {
                nextArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.slider) this.slider.next();
                });
            }

            if (!this.hasInitialized) {
                this.hasInitialized = true;

                setTimeout(() => {
                    this.container.classList.add("keen-slider--initialized")
                }, 300);

            }
        }

        destroySlider() {
            if (this.slider) {
                this.slider.destroy();
                this.slider = null;
            }
            this.hasInitialized = false;
            this.isAtStart = true;
            this.isAtEnd = false;
        }

        handleSlideChange(slider, container) {
            // container.classList.add("keen-slider--initialized");
            
            // Update slide states
            slider.slides.forEach((element, idx) => {
                const portion = slider.track.details.slides[idx].portion;
                // element.classList.toggle('keen-slider__slide--active', portion === 1);
            });
        }

        disconnectedCallback() {
            // Clean up resize listener
            window.removeEventListener('resize', this.handleResize.bind(this));
            
            // Clear timeout
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            
            // Destroy slider
            this.destroySlider();
        }
    }

    window.customElements.define('media-grid-carousel', MediaGridCarousel);
    
}